import { useState, useEffect, useRef, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { Button } from '@praktio/common-ui';
import { Constants } from '@praktio/common';

import { showToC as setShowToC } from '../../../ducks/toc';

import './ToC.scss';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { setAuthorShowToC, setAuthorSlideId } from '../../../utils/functions';
import { updateLearnerStatus, getLearnerCertificate } from '../../../ducks/learner';

interface IProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  currentSlideId?: string;
  course: ICourse;
  showToC: boolean;
  showReviewRetake: boolean;
  loaded?: boolean;
}

interface ISlideClick {
  courseId: string;
  moduleId: string;
  slideId: string;
}

const { TimeEventTypes, TemplateTypes } = Constants;

const IndividualSlide = ({
  courseId,
  moduleId,
  slide,
  onClick,
  moduleUnlocked,
  currentSlideId,
  showReviewRetake,
  priorSlideCompleted,
}: {
  courseId: string;
  moduleId: string;
  slide: ISlide;
  onClick: ({ courseId, moduleId, slideId }: ISlideClick) => void;
  moduleUnlocked?: boolean;
  currentSlideId?: string;
  showReviewRetake: boolean;
  priorSlideCompleted?: boolean;
}) => {
  const { _id, name, progress, type } = slide;
  const { completed, started, correct } = progress || {};
  const disabled = type === TemplateTypes.Conclusions && !completed;
  if ((!moduleUnlocked && !priorSlideCompleted) || disabled) {
    return (
      <div className={classNames('ToC-slide', 'locked')} key={_id}>
        <span className={classNames('material-icons ToC-icon twenty-px-centered')}>lock</span>
        <div className="ToC-slide-name">{name}</div>
      </div>
    );
  }

  return (
    <div
      className={classNames('ToC-slide', 'unlocked', {
        current: currentSlideId === _id,
        showReviewRetake,
      })}
      key={_id}
      onClick={() => onClick({ courseId, moduleId, slideId: _id })}
    >
      <span
        className={classNames('material-icons ToC-icon twenty-px-centered', {
          correct: completed && correct,
          incorrect: completed && !correct,
        })}
      >
        {completed ? (correct ? 'check' : 'close') : started ? 'incomplete_circle' : 'play_arrow'}
      </span>
      <div className="ToC-slide-name">{name}</div>
    </div>
  );
};

const IndividualModule = ({
  course,
  module,
  index,
  onSlideClick,
  currentSlideId,
  onLinkClick,
  currentModuleId,
  showReviewRetake,
  loaded,
}: {
  course: ICourse;
  module: IModule;
  index: number;
  onSlideClick: ({ courseId, moduleId, slideId }: ISlideClick) => void;
  currentSlideId?: string;
  onLinkClick: (id: string, index: number) => void;
  currentModuleId: string;
  showReviewRetake: boolean;
  loaded?: boolean;
}) => {
  const { modulesInOrder } = course;
  const {
    _id,
    completed,
    completedOverride,
    started,
    title,
    isOptional,
    isReleased,
    isUnlocked,
    timeReleasePeriod,
    takeAnytime,
    tooltipText,
    unlockDate,
    isRetake: retaking,
  } = module;
  const moduleComplete = completedOverride || completed;
  const moduleRef = useRef<HTMLDivElement>(null);
  const [moduleExpanded, setModuleExpanded] = useState(false);
  const [manualCollapse, setManualCollapse] = useState(false);
  const slides = loaded ? module.slides : undefined;
  useEffect(() => {
    if (_id === currentModuleId && slides?.length && !manualCollapse) {
      setModuleExpanded(true);
    } else if (_id !== currentModuleId) {
      setManualCollapse(false);
      setModuleExpanded(false);
    }
  }, [_id, currentModuleId, slides, manualCollapse]);

  let isAvailable = !timeReleasePeriod || isReleased;

  let reasonModuleIsNotAvailable = '';

  let timeLocked = false;
  if (unlockDate) {
    const date = new Date(unlockDate);
    const now = new Date();
    if (date > now) {
      reasonModuleIsNotAvailable = `Available on ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
      timeLocked = true;
    }
  }

  // If the parent course has the in-order-completion requirement and this module does not have
  // the "take anytime" flag set to true and the isUnlocked flag is not set to true, we must
  // determine its availablility.
  if (modulesInOrder && !takeAnytime && !isUnlocked) {
    let found = false;
    let index = 0;
    let incompletePriorModules = 0;

    // Loop through entire list of modules in course until reaching this module
    while (!found) {
      const priorModule = course.modules[index];

      // When we reach this module in the loop, we don't need to keep checking further.
      // (If this module is the first, we don't need to make it unavailable no matter what.);
      if (priorModule._id === _id) {
        found = true;

        // If, by the time we have reached this module in the course list, there have been found
        // prior modules that are required and incomplete, this module needs tooltip text to
        // tell the user why it is unavailable. (isAvailable has already been set.)
        if (incompletePriorModules > 0) {
          if (timeLocked) {
            reasonModuleIsNotAvailable += `, if all prior, required modules are completed`;
          } else {
            reasonModuleIsNotAvailable = `Available after completing prior, required modules`;
          }
        }
        // If a module is reached on this looping that is not optional and not complete,
        // we know all the following modules (including this one) should not be available.
        // The exception being if this module does not meet the criteria at the beginning
        // of the If statement (Course is not in-order completion, take anytime flag is checked, or isUnlocked)
      } else if (
        !priorModule.isOptional &&
        !(priorModule.completedOverride || priorModule.completed)
      ) {
        isAvailable = false;
        incompletePriorModules += 1;
      }
      index += 1;
    }
  }

  const userIsOnThisModule = currentModuleId === _id;
  const nonSlideModule = !slides || !slides.length;

  const modifyHoverableClass = (operation: string) => {
    if (moduleRef && moduleRef.current) {
      switch (operation) {
        case 'add':
          moduleRef.current.classList.add('hoverable');
          break;
        case 'remove':
          moduleRef.current.classList.remove('hoverable');
          break;
      }
    }
  };

  let slidesList = undefined;
  if (slides && moduleExpanded) {
    slidesList = [];
    let priorSlide;
    for (const slide of slides) {
      slidesList.push(
        <IndividualSlide
          key={slide._id}
          courseId={course._id as string}
          moduleId={_id as string}
          slide={slide}
          onClick={onSlideClick}
          moduleUnlocked={isUnlocked}
          currentSlideId={currentSlideId}
          showReviewRetake={showReviewRetake}
          priorSlideCompleted={!priorSlide || !!priorSlide?.progress?.completed}
        />,
      );

      priorSlide = slide;
    }
  }

  const isHighlighted =
    userIsOnThisModule && (!currentSlideId || nonSlideModule || !moduleExpanded);

  return (
    <div key={_id} className="ToC-module text-left d-flex flex-column">
      {!!isAvailable && (
        <>
          <Link
            to={userIsOnThisModule ? '#' : `/courses/${course._id}/modules/${_id}`}
            onClick={() => {
              // The user shouln't be able to click the module when they are in
              // a slide within.
              if (!userIsOnThisModule) {
                onLinkClick(_id as string, index);
              }
            }}
            className={classNames('ToC-link', {
              'pointer-events-none': userIsOnThisModule,
            })}
            aria-label={`${title} Module`}
            aria-current={isHighlighted}
          >
            <div
              ref={moduleRef}
              className={classNames('ToC-module-details hoverable', {
                // The module should be highlighted if the user is in the module but
                //  is not on a slide that is within it OR if the module has no slides associated
                // (non-slide type module like Diligence Simulation)
                'module-highlighted': isHighlighted,
                'user-on-module': userIsOnThisModule,
              })}
            >
              {!!slides && slides.length > 0 && (
                <span
                  onClick={(event) => {
                    event?.preventDefault();
                    if (userIsOnThisModule) {
                      setModuleExpanded(!moduleExpanded);
                      setManualCollapse(moduleExpanded);
                    }
                  }}
                  onMouseEnter={() => modifyHoverableClass('remove')}
                  onMouseLeave={() => modifyHoverableClass('add')}
                  className={classNames(
                    'material-icons',
                    'ToC-icon',
                    'pointer',
                    'expand_chevron',
                    'twenty-px-centered',
                    {
                      invisible: !userIsOnThisModule,
                    },
                  )}
                >
                  {moduleExpanded ? 'expand_more' : 'chevron_right'}
                </span>
              )}
              {(!slides || !slides.length) && <div className="ToC-icon twenty-px-centered" />}
              <span
                className={classNames('material-icons', 'ToC-icon', 'twenty-px-centered', {
                  completed: moduleComplete,
                  retaking,
                })}
              >
                {moduleComplete
                  ? retaking
                    ? 'incomplete_circle'
                    : 'check'
                  : started
                  ? 'incomplete_circle'
                  : 'play_arrow'}
              </span>
              <div className="ToC-module-name-and-optional-flag d-flex flex-column">
                <div className="ToC-module-name">{title}</div>
                {!!isOptional && <div className="ToC-optional">Optional</div>}
              </div>
              {!!tooltipText && (
                <OverlayTrigger
                  key={`${_id}-tooltip`}
                  placement="right"
                  overlay={
                    <Popover id={`tooltip-for-${_id}`} className="ToC__Tooltip">
                      <Popover.Content>{tooltipText}</Popover.Content>
                    </Popover>
                  }
                >
                  <span
                    onMouseEnter={() => modifyHoverableClass('remove')}
                    onMouseLeave={() => modifyHoverableClass('add')}
                    className={classNames(
                      'material-icons-outlined',
                      'pull-right',
                      'pointer',
                      'ToC-module-tooltip',
                      'twenty-px-centered',
                    )}
                    onClick={(event) => event?.preventDefault()}
                  >
                    info
                  </span>
                </OverlayTrigger>
              )}
              {!tooltipText && <div className="ToC-module-tooltip-placeholder"></div>}
            </div>
          </Link>
          {!!slides && !!moduleExpanded && (
            <div className="ToC-module-slides d-flex flex-column">{slidesList}</div>
          )}
        </>
      )}
      {!isAvailable && (
        <OverlayTrigger
          key={`${_id}-tooltip`}
          placement="top"
          overlay={
            <Popover id={`tooltip-for-${_id}`}>
              <Popover.Content>{`${reasonModuleIsNotAvailable}.`}</Popover.Content>
            </Popover>
          }
        >
          <div className={classNames('ToC-module-details', 'locked', 'pointer')}>
            <div className="ToC-icon twenty-px-centered" />
            <span className={classNames('material-icons', 'ToC-icon', 'twenty-px-centered')}>
              lock
            </span>
            <div className="ToC-module-name-and-optional-flag d-flex flex-column">
              <div className="ToC-module-name">{title}</div>
              {!!isOptional && <div className="ToC-optional">Optional</div>}
            </div>
            {/* Dev Specs indicate that there should be no tooltip icon for unavailable modules. */}
            {/* Putting in a space-taking div to maintain proper alignment. */}
            <div className="ToC-module-tooltip-placeholder"></div>
          </div>
        </OverlayTrigger>
      )}
    </div>
  );
};

const ToC = forwardRef<HTMLDivElement, IProps>(
  ({ iframeRef, currentSlideId, course, showToC, showReviewRetake, loaded }, ref) => {
    const navigate = useNavigate();
    const params = useParams();
    const courseId = params.courseId as string;
    const moduleId = params.moduleId as string;

    const dispatch = useDispatch();
    const [downloadDisabled, setDownloadDisabled] = useState(false);
    const { title: courseTitle, progress } = course;

    const onSlideClick = (slideData: { courseId: string; moduleId: string; slideId: string }) => {
      if (slideData.moduleId !== moduleId || slideData.courseId !== courseId) {
        navigate(`/courses/${slideData.courseId}/modules/${slideData.moduleId}`);
      } else {
        setAuthorSlideId(iframeRef, slideData.slideId);
      }
    };

    const list: any[] = [];
    // Build out in-order list of categories/modules
    course.modules?.forEach((m: IModule) => {
      if (m.categoryId) {
        let category = list.find((d) => d._id === m.categoryId);
        if (!category) {
          category = course.categories?.find((c) => c._id === m.categoryId);
          if (category) {
            list.push({ ...cloneDeep(category), modules: [] });
          }
        }

        category = list.find((d) => d._id === m.categoryId);
        if (!category) {
          list.push(m);
        } else {
          category.modules.push(m);
        }
      } else {
        list.push(m);
      }
    });

    // Generate rendered components of above list
    const content: any[] = [];
    let index = 0;
    let currentModuleIndex: number | undefined;
    const onLinkClick = (id: string, index: number) => {
      if (moduleId && (currentModuleIndex || currentModuleIndex === 0)) {
        // Send transition status when moving to another module if on
        // a current module
        dispatch(
          updateLearnerStatus({
            courseId,
            moduleId,
            lastActive: new Date(),
            eventType: currentModuleIndex < index ? TimeEventTypes.Next : TimeEventTypes.Back,
          }),
        );
      }
    };

    // Put the course info link as the first in the list in the ToC
    content.push(
      <div key="CourseInfo" className="ToC-module text-left d-flex flex-column">
        <Link
          className="ToC-link"
          to={`/courses/${course._id}`}
          aria-label="Course Information Page"
          aria-current={!moduleId}
        >
          <div className={classNames('ToC-module-details', { 'module-highlighted': !moduleId })}>
            <span className="ToC-icon twenty-px-centered invisible"></span>
            <span className="material-icons ToC-icon twenty-px-centered">web</span>
            <div className="ToC-module-name">Course Information</div>
          </div>
        </Link>
      </div>,
    );

    list.forEach((modOrCat) => {
      let component;
      // If there are modules, it's a category so render children
      if (modOrCat.modules) {
        component = (
          <div key={modOrCat._id} className="ToC-category">
            <div className="ToC-category-name">
              <span>{modOrCat.name}</span>
            </div>
            <div className="ToC-category-content d-flex flex-column">
              {modOrCat.modules.map((childMod: IModule) => {
                const id = childMod._id as string;
                index += 1;
                if (id === moduleId) {
                  currentModuleIndex = index;
                }

                return (
                  <IndividualModule
                    course={course}
                    module={childMod}
                    index={index}
                    onSlideClick={onSlideClick}
                    currentSlideId={currentSlideId}
                    onLinkClick={onLinkClick}
                    currentModuleId={moduleId}
                    showReviewRetake={showReviewRetake}
                    loaded={loaded}
                  />
                );
              })}
            </div>
          </div>
        );
      } else {
        const id = modOrCat._id as string;
        index += 1;
        if (id === moduleId) {
          currentModuleIndex = index;
        }

        component = (
          <IndividualModule
            key={modOrCat._id}
            course={course}
            module={modOrCat}
            index={index}
            onSlideClick={onSlideClick}
            currentSlideId={currentSlideId}
            onLinkClick={onLinkClick}
            currentModuleId={moduleId}
            showReviewRetake={showReviewRetake}
            loaded={loaded}
          />
        );
      }

      content.push(component);
    });

    const percentage = `${(progress || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;

    if (progress === 100 && course.certUponComplete) {
      content.push(
        <button
          key="ModuleDetails"
          className={classNames(
            'ToC-module-details align-items-center download-cert-button',
            `${downloadDisabled ? 'not-allowed' : 'pointer'}`,
            { locked: downloadDisabled },
          )}
          onClick={() => {
            if (!downloadDisabled) {
              setDownloadDisabled(true);
              (dispatch(getLearnerCertificate({ courseId, title: courseTitle })) as any).then(() =>
                setDownloadDisabled(false),
              );
            }
          }}
          aria-pressed={downloadDisabled}
          aria-disabled={downloadDisabled}
        >
          <span className="ToC-icon twenty-px-centered invisible"></span>
          <span className="material-icons ToC-icon twenty-px-centered ToC-download-icon">
            download
          </span>
          <div className="ToC-module-name">Certificate</div>
          {!!downloadDisabled && (
            <Spinner animation="border" size="sm" role="status" className="ml-2">
              <span className="sr-only">Loading...</span>
            </Spinner>
          )}
        </button>,
      );
    }

    return (
      <div ref={ref} className="ToC d-flex flex-column">
        <div className="ToC-progress d-flex flex-column sticky-top">
          <div className="ToC-progress-label d-flex justify-content-between">
            <h2 className="course-title">{courseTitle || 'Course Progress'}</h2>

            {!!moduleId && (
              <button
                className="ToC-close-button"
                onClick={() => {
                  setAuthorShowToC(iframeRef, false);
                  dispatch(setShowToC(false));
                }}
              >
                <span className="material-icons">close</span>
              </button>
            )}
          </div>

          <div className="ToC-progress-bar-wrapper">
            <div className="ToC-progress-bar">
              <div className="ToC-progress-bar-inner" style={{ width: `${progress}%` }}></div>
            </div>
            <div>{percentage}</div>
          </div>
        </div>
        <div className="ToC-non-progress-content dark-scroll">
          <div className="ToC-content d-flex flex-column">{content}</div>
          {!showToC && (
            <button
              className="ToC-open-button"
              onClick={() => {
                setAuthorShowToC(iframeRef, true);
                dispatch(setShowToC(true));
              }}
            >
              <span className="material-icons">toc</span>
            </button>
          )}
        </div>
      </div>
    );
  },
);

export default ToC;
